import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  ArrowLeft, Settings, LogOut, Bell, Music, Users, 
  TrendingUp, Download, Key, BarChart3, Headphones,
  CheckCircle, Calendar, Award, Wrench, X, MessageSquareQuote,
  Send, Mail, Trash2, KeyRound
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Admin = () => {
  const { token, user, logout } = useAuth();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [clients, setClients] = useState([]);
  const [clientFilter, setClientFilter] = useState('all'); // all | active | inactive
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [showFixModal, setShowFixModal] = useState(false);
  const [fixEmail, setFixEmail] = useState('');
  const [fixTheme, setFixTheme] = useState('1');
  const [fixing, setFixing] = useState(false);

  const handleLogout = () => {
    logout();
    toast.success('Déconnexion réussie');
    navigate('/login');
  };

  useEffect(() => {
    fetchDashboard();
    fetchClients();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(response.data);
    } catch (error) {
      console.error('Erreur chargement dashboard:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/export/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data.clients || []);
    } catch (error) {
      console.error('Erreur chargement clients:', error);
    }
  };

  const getActivityStatus = (lastActive) => {
    if (!lastActive) return { label: 'Jamais connectée', color: 'bg-red-100 text-red-700', dot: 'bg-red-500' };
    const now = new Date();
    const last = new Date(lastActive);
    const diffDays = Math.floor((now - last) / (1000 * 60 * 60 * 24));
    if (diffDays <= 7) return { label: `Active il y a ${diffDays}j`, color: 'bg-green-100 text-green-700', dot: 'bg-green-500' };
    if (diffDays <= 30) return { label: `Inactive (${diffDays}j)`, color: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' };
    return { label: `Inactive (${diffDays}j)`, color: 'bg-red-100 text-red-700', dot: 'bg-red-500' };
  };

  const filteredClients = clients.filter(c => {
    if (clientFilter === 'all') return true;
    const isActive = c.last_active && ((new Date() - new Date(c.last_active)) / (1000*60*60*24)) <= 7;
    return clientFilter === 'active' ? isActive : !isActive;
  });

  const handleRelance = (email, currentTheme = 1) => {
    const subject = encodeURIComponent('Votre programme Déclic RePas vous attend');
    const body = encodeURIComponent(
`Bonjour,

Votre programme Déclic RePas vous attend. Vous en êtes à la séance ${currentTheme}/11 — chaque écoute compte pour installer le changement.

Pas besoin de tout faire d'un coup. Même 15 minutes aujourd'hui, c'est un pas de plus vers vos objectifs.

Reprenez votre séance ici : https://app-declic-repas.fr

À bientôt,
Lynda Bry-Bonnard
Votre hypnothérapeute`
    );
    window.open(`mailto:${email}?subject=${subject}&body=${body}`, '_blank');
  };

  const handleRelanceAll = () => {
    const inactives = clients.filter(c => !c.last_active || ((new Date() - new Date(c.last_active)) / (1000*60*60*24)) > 7);
    if (inactives.length === 0) { toast('Aucune cliente inactive'); return; }
    const emails = inactives.map(c => c.email).join(',');
    const subject = encodeURIComponent('Votre programme Déclic RePas vous attend');
    const body = encodeURIComponent(
`Bonjour,

Votre programme Déclic RePas vous attend. Chaque écoute compte pour installer le changement.

Pas besoin de tout faire d'un coup. Même 15 minutes aujourd'hui, c'est un pas de plus vers vos objectifs.

Reprenez votre séance ici : https://app-declic-repas.fr

À bientôt,
Lynda Bry-Bonnard
Votre hypnothérapeute`
    );
    window.open(`mailto:?bcc=${emails}&subject=${subject}&body=${body}`, '_blank');
  };

  const handleDeleteUser = async (email) => {
    if (!window.confirm(`Supprimer le compte ${email} et toutes ses données ? Cette action est irréversible.`)) return;
    try {
      await axios.delete(`${API_URL}/admin/users/${encodeURIComponent(email)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Compte ${email} supprimé`);
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur suppression');
    }
  };

  const handleResetPassword = async (email) => {
    try {
      const response = await axios.post(`${API_URL}/admin/users/${encodeURIComponent(email)}/reset-password`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const { temp_password } = response.data;
      const subject = encodeURIComponent('Votre nouveau mot de passe Déclic RePas');
      const body = encodeURIComponent(
`Bonjour,

Voici votre nouveau mot de passe pour accéder à Déclic RePas :

${temp_password}

Connectez-vous ici : https://app-declic-repas.fr

À bientôt,
Lynda`
      );
      window.open(`mailto:${email}?subject=${subject}&body=${body}`, '_blank');
      toast.success(`Mot de passe réinitialisé — email pré-rempli ouvert`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur réinitialisation');
    }
  };

  const exportClients = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API_URL}/admin/export/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Créer le CSV
      const clients = response.data.clients;
      const headers = ['Email', 'Date inscription', 'Thème actuel', 'Progression %', 'Écoutes', 'Streak actuel', 'Dernière activité'];
      const csvContent = [
        headers.join(';'),
        ...clients.map(c => [
          c.email,
          c.created_at ? new Date(c.created_at).toLocaleDateString('fr-FR') : '',
          `${c.current_theme}/11`,
          `${c.progress_percent}%`,
          c.completed_listens,
          c.current_streak,
          c.last_active || ''
        ].join(';'))
      ].join('\n');
      
      // Télécharger
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `clients_declic_repas_${new Date().toISOString().slice(0,10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);
      
      toast.success(`${clients.length} clients exportés !`);
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    } finally {
      setExporting(false);
    }
  };

  const handleFixProgress = async () => {
    if (!fixEmail) {
      toast.error('Veuillez entrer un email');
      return;
    }
    
    setFixing(true);
    try {
      const response = await axios.post(
        `${API_URL}/admin/fix-progress-all`,
        null,
        {
          params: {
            email: fixEmail,
            up_to_theme: parseInt(fixTheme) - 1
          },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success(response.data.message);
      setShowFixModal(false);
      setFixEmail('');
      setFixTheme('1');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la correction');
    } finally {
      setFixing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-sage-500 font-sans">Chargement...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24 bg-sand-50" data-testid="admin-page">
      {/* Header */}
      <div className="bg-sage-500 px-6 py-8 text-white">
        <div className="flex items-center justify-between mb-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/aide')}
            className="text-white hover:bg-sage-600"
            data-testid="admin-back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className="text-white hover:bg-sage-600"
            data-testid="admin-logout-button"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Déconnexion
          </Button>
        </div>
        <h1 className="text-4xl font-serif font-light mb-2" data-testid="admin-title">
          Dashboard
        </h1>
        <p className="text-white/80 font-sans">{user?.email}</p>
      </div>

      {/* Stats principales */}
      {dashboard && (
        <>
          <div className="px-6 py-6 grid grid-cols-2 gap-3">
            <div className="bg-white rounded-xl border border-sand-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <Users className="w-5 h-5 text-sage-500" />
                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                  +{dashboard.users.new_this_month} ce mois
                </span>
              </div>
              <p className="text-3xl font-serif font-light text-sage-700">
                {dashboard.users.total}
              </p>
              <p className="text-xs text-sage-500">Clients total</p>
            </div>
            
            <div className="bg-white rounded-xl border border-sand-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <TrendingUp className="w-5 h-5 text-declic" />
              </div>
              <p className="text-3xl font-serif font-light text-sage-700">
                {dashboard.users.active_7_days}
              </p>
              <p className="text-xs text-sage-500">Actifs (7 jours)</p>
            </div>
            
            <div className="bg-white rounded-xl border border-sand-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
              </div>
              <p className="text-3xl font-serif font-light text-sage-700">
                {dashboard.users.completed_program}
              </p>
              <p className="text-xs text-sage-500">Programme terminé</p>
            </div>
            
            <div className="bg-white rounded-xl border border-sand-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <Headphones className="w-5 h-5 text-purple-500" />
              </div>
              <p className="text-3xl font-serif font-light text-sage-700">
                {dashboard.engagement.total_listens}
              </p>
              <p className="text-xs text-sage-500">Écoutes totales</p>
            </div>
          </div>

          {/* Progression moyenne */}
          <div className="px-6 pb-4">
            <div className="bg-white rounded-xl border border-sand-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center">
                  <BarChart3 className="w-5 h-5 text-sage-500 mr-2" />
                  <span className="font-medium text-sage-700">Progression moyenne</span>
                </div>
                <span className="text-2xl font-serif text-sage-700">
                  {dashboard.engagement.avg_theme_progress}/11
                </span>
              </div>
              <div className="h-3 bg-sage-100 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-sage-500 to-declic rounded-full transition-all"
                  style={{ width: `${(dashboard.engagement.avg_theme_progress / 11) * 100}%` }}
                />
              </div>
              <p className="text-xs text-sage-500 mt-2">
                Taux de complétion : {dashboard.engagement.completion_rate}%
              </p>
            </div>
          </div>

          {/* Codes d'accès */}
          <div className="px-6 pb-4">
            <div className="bg-white rounded-xl border border-sand-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center">
                  <Key className="w-5 h-5 text-amber-500 mr-2" />
                  <span className="font-medium text-sage-700">Codes d'accès</span>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-green-50 rounded-lg p-2">
                  <p className="text-xl font-serif text-green-700">{dashboard.codes.available}</p>
                  <p className="text-xs text-green-600">Disponibles</p>
                </div>
                <div className="bg-sage-50 rounded-lg p-2">
                  <p className="text-xl font-serif text-sage-700">{dashboard.codes.used}</p>
                  <p className="text-xs text-sage-600">Utilisés</p>
                </div>
                <div className="bg-sand-100 rounded-lg p-2">
                  <p className="text-xl font-serif text-sage-700">{dashboard.codes.total}</p>
                  <p className="text-xs text-sage-600">Total</p>
                </div>
              </div>
            </div>
          </div>

          {/* ====== LISTE DES CLIENTES ====== */}
          <div className="px-6 pb-4">
            <div className="bg-white rounded-xl border border-sand-200 p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <Users className="w-5 h-5 text-sage-500 mr-2" />
                  <span className="font-medium text-sage-700">Mes clientes ({clients.length})</span>
                </div>
                <Button
                  onClick={handleRelanceAll}
                  size="sm"
                  className="bg-declic hover:bg-declic/90 text-white h-8 rounded-lg text-xs px-3"
                  data-testid="relance-all-button"
                >
                  <Mail className="w-3 h-3 mr-1" />
                  Relancer les inactives
                </Button>
              </div>

              {/* Filtres */}
              <div className="flex gap-2 mb-4">
                {[
                  { key: 'all', label: 'Toutes' },
                  { key: 'active', label: 'Actives' },
                  { key: 'inactive', label: 'Inactives' },
                ].map(f => (
                  <button
                    key={f.key}
                    onClick={() => setClientFilter(f.key)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      clientFilter === f.key
                        ? 'bg-sage-500 text-white'
                        : 'bg-sage-50 text-sage-600 hover:bg-sage-100'
                    }`}
                    data-testid={`client-filter-${f.key}`}
                  >
                    {f.label}
                    {f.key === 'inactive' && clients.length > 0 && (
                      <span className="ml-1">
                        ({clients.filter(c => !c.last_active || ((new Date() - new Date(c.last_active)) / (1000*60*60*24)) > 7).length})
                      </span>
                    )}
                  </button>
                ))}
              </div>

              {/* Liste */}
              <div className="space-y-2 max-h-[500px] overflow-y-auto" data-testid="client-list">
                {filteredClients.length === 0 ? (
                  <p className="text-sage-400 text-sm text-center py-4">Aucune cliente dans ce filtre</p>
                ) : (
                  filteredClients.map((client, idx) => {
                    const activity = getActivityStatus(client.last_active);
                    const progressPct = client.progress_percent;
                    return (
                      <div key={idx} className="border border-sand-200 rounded-lg p-3" data-testid={`client-row-${idx}`}>
                        <div className="flex items-start justify-between mb-2">
                          <div className="min-w-0 flex-1">
                            <p className="text-sage-700 text-sm font-medium truncate">{client.email}</p>
                            <p className="text-sage-400 text-xs">
                              Inscrite le {client.created_at ? new Date(client.created_at).toLocaleDateString('fr-FR') : '—'}
                            </p>
                          </div>
                          <span className={`flex-shrink-0 ml-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${activity.color}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${activity.dot}`} />
                            {activity.label}
                          </span>
                        </div>

                        {/* Barre progression */}
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 bg-sage-100 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all ${progressPct > 0 ? 'bg-declic' : 'bg-sage-200'}`}
                              style={{ width: `${Math.max(progressPct, 2)}%` }}
                            />
                          </div>
                          <span className="text-xs text-sage-500 font-medium w-12 text-right">
                            {client.current_theme}/{client.total_themes}
                          </span>
                        </div>

                        {/* Stats mini + relance */}
                        <div className="flex items-center justify-between mt-2">
                          <div className="flex items-center gap-4 text-xs text-sage-400">
                            <span className="flex items-center gap-1">
                              <Headphones className="w-3 h-3" />
                              {client.completed_listens} écoutes
                            </span>
                            {client.current_streak > 0 && (
                              <span className="flex items-center gap-1">
                                <Award className="w-3 h-3" />
                                Streak {client.current_streak}j
                              </span>
                            )}
                          </div>
                          {activity.dot !== 'bg-green-500' && (
                            <button
                              onClick={() => handleRelance(client.email, client.current_theme)}
                              className="flex items-center gap-1 text-xs text-declic hover:text-declic/70 font-medium transition-colors"
                              data-testid={`relance-btn-${idx}`}
                            >
                              <Send className="w-3 h-3" />
                              Relancer
                            </button>
                          )}
                        </div>

                        {/* Actions : MDP + Supprimer */}
                        <div className="flex items-center gap-3 mt-2 pt-2 border-t border-sage-100">
                          <button
                            onClick={() => handleResetPassword(client.email)}
                            className="flex items-center gap-1 text-xs text-sage-400 hover:text-sage-600 transition-colors"
                            data-testid={`reset-pwd-btn-${idx}`}
                          >
                            <KeyRound className="w-3 h-3" />
                            Nouveau MDP
                          </button>
                          <button
                            onClick={() => handleDeleteUser(client.email)}
                            className="flex items-center gap-1 text-xs text-red-400 hover:text-red-600 transition-colors"
                            data-testid={`delete-user-btn-${idx}`}
                          >
                            <Trash2 className="w-3 h-3" />
                            Supprimer
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Actions principales */}
      <div className="px-6 py-4 space-y-3">
        <Button
          onClick={exportClients}
          disabled={exporting}
          className="w-full h-14 bg-declic hover:bg-declic/90 text-white rounded-xl text-base font-sans font-semibold"
        >
          <Download className="w-5 h-5 mr-3" />
          {exporting ? 'Export en cours...' : 'Exporter la liste clients (CSV)'}
        </Button>
        
        <Button
          onClick={() => navigate('/admin/codes')}
          className="w-full h-14 bg-sage-500 hover:bg-sage-600 text-white rounded-xl text-base font-sans font-semibold"
          data-testid="admin-codes-button"
        >
          <Key className="w-5 h-5 mr-3" />
          Codes d'accès
        </Button>
        
        <Button
          onClick={() => navigate('/admin/contents')}
          variant="outline"
          className="w-full h-14 border-sage-300 text-sage-700 hover:bg-sage-50 rounded-xl text-base font-sans font-semibold"
        >
          <Settings className="w-5 h-5 mr-3" />
          Gérer les Contenus
        </Button>
        
        <Button
          onClick={() => navigate('/admin/announcements')}
          variant="outline"
          className="w-full h-14 border-sage-300 text-sage-700 hover:bg-sage-50 rounded-xl text-base font-sans font-semibold"
          data-testid="admin-announcements-button"
        >
          <Bell className="w-5 h-5 mr-3" />
          Gérer les Annonces
        </Button>
        
        <Button
          onClick={() => navigate('/admin/testimonials')}
          variant="outline"
          className="w-full h-14 border-sage-300 text-sage-700 hover:bg-sage-50 rounded-xl text-base font-sans font-semibold"
          data-testid="admin-testimonials-button"
        >
          <MessageSquareQuote className="w-5 h-5 mr-3" />
          Gérer les Témoignages
        </Button>
        
        <Button
          onClick={() => navigate('/admin/audio')}
          variant="outline"
          className="w-full h-14 border-sage-300 text-sage-700 hover:bg-sage-50 rounded-xl text-base font-sans font-semibold"
          data-testid="admin-audio-button"
        >
          <Music className="w-5 h-5 mr-3" />
          Fichiers Audio
        </Button>
        
        <Button
          onClick={() => setShowFixModal(true)}
          variant="outline"
          className="w-full h-14 border-amber-300 text-amber-700 hover:bg-amber-50 rounded-xl text-base font-sans font-semibold"
          data-testid="admin-fix-progress-button"
        >
          <Wrench className="w-5 h-5 mr-3" />
          Corriger progression client
        </Button>
      </div>

      {/* Modal Corriger progression */}
      {showFixModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-serif text-sage-700">Corriger progression</h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowFixModal(false)}
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <p className="text-sm text-sage-500 mb-4">
              Marquer les séances comme écoutées pour un client qui a eu un problème technique.
            </p>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="fix-email" className="text-sage-700">Email du client</Label>
                <Input
                  id="fix-email"
                  type="email"
                  value={fixEmail}
                  onChange={(e) => setFixEmail(e.target.value)}
                  placeholder="client@example.com"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="fix-theme" className="text-sage-700">Marquer comme écouté jusqu'à la séance n°</Label>
                <select
                  id="fix-theme"
                  value={fixTheme}
                  onChange={(e) => setFixTheme(e.target.value)}
                  className="mt-1 w-full h-10 px-3 border border-sand-200 rounded-md text-sage-700"
                >
                  {[1,2,3,4,5,6,7,8,9,10,11].map(n => (
                    <option key={n} value={n}>Séance {n}</option>
                  ))}
                </select>
                <p className="text-xs text-sage-400 mt-1">
                  Ex: "Séance 3" marquera les séances 1, 2 et 3 comme complétées
                </p>
              </div>
              
              <Button
                onClick={handleFixProgress}
                disabled={fixing || !fixEmail}
                className="w-full h-12 bg-amber-500 hover:bg-amber-600 text-white rounded-xl"
              >
                {fixing ? 'Correction en cours...' : 'Corriger la progression'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
